import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Thermometer,
  Truck,
  FileText,
  GraduationCap,
  Snowflake,
  BadgeCheck,
  MapPin,
  Sparkles,
  CalendarClock,
  Siren,
  Clock,
  Lock,
  TrendingUp,
  Store,
  Package,
  Shirt,
  Warehouse,
  Workflow,
  ClipboardList,
  ShoppingCart,
  Map as MapIcon,
  Wrench,
  Boxes,
  Timer,
  Route,
  AlertTriangle,
  PiggyBank,
  Layers,
  Leaf,
  Ship,
  Globe,
  type LucideIcon,
} from 'lucide-react'
import { HeroSection } from '@/components/sections/HeroSection'
import { QuoteCTA } from '@/components/sections/QuoteCTA'
import { Section } from '@/components/ui/Section'
import { Container } from '@/components/ui/Container'
import { SectionTitle } from '@/components/ui/SectionTitle'
import { ServiceCard } from '@/components/ui/ServiceCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate'

const serviceImages: Record<string, string> = {
  pharma: '/images/service-pharma.jpg',
  food: '/images/service-food.jpg',
  security: '/images/fleet-05.jpg',
  fashion: '/images/service-fashion.jpg',
  logistics: '/images/service-logistics.jpg',
  tempControl: '/images/service-temp-control.jpg',
  ftl: '/images/company-fleet.jpg',
  groupage: '/images/service-groupage.jpg',
  intermodal: '/images/service-intermodal.jpg',
}

const serviceSideImage: Record<string, string> = {
  pharma: '/images/service-plasma.jpg',
  food: '/images/company-warehouse.jpg',
  security: '/images/company-operations.jpg',
  fashion: '/images/fleet-02.jpg',
  logistics: '/images/service-logistics-2.jpg',
  tempControl: '/images/fleet-01.jpg',
  ftl: '/images/fleet-04.jpg',
  groupage: '/images/company-warehouse.jpg',
  intermodal: '/images/fleet-01.jpg',
}

const featureIconsByService: Record<string, LucideIcon[]> = {
  pharma: [ShieldCheck, Thermometer, Truck, FileText, GraduationCap],
  food: [Snowflake, BadgeCheck, MapPin, Sparkles, CalendarClock],
  security: [MapPin, Siren, Clock, ShieldCheck, Lock],
  fashion: [TrendingUp, Store, Truck, Package, Shirt],
  logistics: [Warehouse, Package, Workflow, ClipboardList, ShoppingCart],
  tempControl: [Snowflake, Thermometer, MapIcon, ShieldCheck, Wrench],
  ftl: [Truck, Boxes, Timer, Route, AlertTriangle],
  groupage: [PiggyBank, Layers, CalendarClock, MapPin, Snowflake],
  intermodal: [Leaf, Ship, Globe, Boxes, ShieldCheck],
}

const validServiceIds = Object.keys(serviceImages)

export default function ServiceDetail() {
  const { serviceId } = useParams<{ serviceId: string }>()
  const { t, i18n } = useTranslation('common')
  const navigate = useLocalizedNavigate()

  if (!serviceId || !validServiceIds.includes(serviceId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center px-4">
          <h1 className="text-2xl font-bold text-brand-dark mb-4">
            {t('error.title')}
          </h1>
          <p className="text-brand-text mb-8">{t('error.description')}</p>
          <button
            onClick={() => navigate('services')}
            className="inline-flex items-center px-6 py-3 bg-brand-red text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('buttons.backToServices')}
          </button>
        </div>
      </div>
    )
  }

  const title = t(`services.detail.${serviceId}.title`)
  const description = t(`services.detail.${serviceId}.description`)
  const features = t(`services.detail.${serviceId}.features`, { returnObjects: true }) as string[]
  const imageSrc = serviceImages[serviceId]
  const sideImage = serviceSideImage[serviceId]

  const relatedIds = validServiceIds.filter((id) => id !== serviceId).slice(0, 3)

  return (
    <>
      <Helmet>
        <title>{`${title} — Chiggiato SpA`}</title>
        <meta name="description" content={description} />
        <html lang={i18n.language} />
      </Helmet>

      <HeroSection
        imageSrc={imageSrc}
        title={title}
        slogan={t('services.hero.slogan')}
        primaryCta={{ label: t('buttons.contactUs'), to: 'contacts' }}
        secondaryCta={{ label: t('nav.services'), to: 'services' }}
      />

      <Section variant="default">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <SectionTitle
                title={t('services.detail.overviewHeading')}
                subtitle={title}
              />
              <p className="text-lg text-brand-text leading-relaxed">
                {description}
              </p>
            </div>
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-xl">
              <img
                src={sideImage}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-dark/30 via-transparent to-transparent" />
            </div>
          </div>
        </Container>
      </Section>

      {Array.isArray(features) && features.length > 0 && (
        <Section variant="light">
          <Container>
            <SectionTitle
              title={t('services.detail.featuresHeading')}
              centered
            />
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {features.map((feature, idx) => {
                const Icon = featureIconsByService[serviceId]?.[idx] ?? ShieldCheck
                return (
                  <li
                    key={idx}
                    className="flex items-start gap-4 bg-white rounded-lg p-5 shadow-sm border border-black/5 hover:shadow-md transition-shadow"
                  >
                    <span className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-lg bg-brand-red/10">
                      <Icon className="w-6 h-6 text-brand-red" strokeWidth={1.75} />
                    </span>
                    <span className="text-brand-text leading-relaxed pt-1.5">
                      {feature}
                    </span>
                  </li>
                )
              })}
            </ul>
          </Container>
        </Section>
      )}

      <Section variant="default">
        <Container>
          <div className="flex flex-wrap items-center justify-between gap-6">
            <button
              onClick={() => navigate('services')}
              className="group inline-flex items-center gap-2 text-brand-red font-medium"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              {t('buttons.backToServices')}
            </button>
            <LocalizedLink
              to="contacts"
              className="group inline-flex items-center gap-2 bg-brand-red text-white px-8 py-3 rounded-lg font-medium hover:bg-brand-red-dark transition-colors"
            >
              {t('buttons.contactUs')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </LocalizedLink>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <SectionTitle
            title={t('services.detail.relatedHeading')}
            centered
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedIds.map((id) => (
              <ServiceCard
                key={id}
                serviceId={id}
                imageSrc={serviceImages[id]}
                title={t(`services.cards.${id}.title`)}
                description={t(`services.cards.${id}.description`)}
              />
            ))}
          </div>
        </Container>
      </Section>

      <QuoteCTA />
    </>
  )
}
